import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  forwardRef,
  inject,
  input,
  signal,
  ViewChild,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { TranslocoModule } from '@jsverse/transloco';
import { FunctionType } from '@olymp/shared';
import { NgxMaskDirective } from 'ngx-mask';
import { DatePicker, DatePickerModule } from 'primeng/datepicker';
import { InputTextModule } from 'primeng/inputtext';
import { isValidDate } from './check-date.util';

@Component({
  selector: 'lib-calendar',
  imports: [
    DatePickerModule,
    TranslocoModule,
    FormsModule,
    InputTextModule,
    NgxMaskDirective,
  ],
  template: `
    <div class="cal-wrap">
      @if (label()) {
        <label class="cal-label">
          {{ label() | transloco }}
          <span class="cal-label__required">*</span>
        </label>
      }
      <div class="cal-input-wrap">
        <p-datepicker
          dataType="string"
          dateFormat="dd.mm.yy"
          styleClass="w-full"
          [placeholder]="placeholder() || 'dd.mm.yyyy' | transloco"
          [(ngModel)]="value"
          #datepicker
          (onSelect)="calendarChange($event)"
          [disabled]="isDisabled()"
          [minDate]="minDate()"
          [appendTo]="'body'"
        />
        <input
          #inputEl
          type="text"
          mask="d0.M0.0000"
          [(ngModel)]="value"
          pInputText
          class="cal-text-input"
          [class.cal-text-input--error]="!isValid()"
          (input)="dateChange($event)"
          (click)="onInputClick($event)"
          [disabled]="isDisabled()"
        />
        <i class="pi pi-calendar cal-icon"></i>
      </div>
    </div>
  `,
  styleUrl: './calendar.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    DatePipe,
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CalendarComponent),
      multi: true,
    },
  ],
})
export class CalendarComponent implements ControlValueAccessor {
  onChange: FunctionType = () => {};

  onTouched: FunctionType = () => {};

  label = input<string>();
  placeholder = input<string>();

  minDate = input<Date>();

  value = signal('');

  isValid = signal(true);
  isDisabled = signal(true);

  private datePipe = inject(DatePipe);

  @ViewChild('datepicker') datepicker!: DatePicker;

  @ViewChild('inputEl') inputRef!: ElementRef<HTMLInputElement>;

  onInputClick(event: Event) {
    this.datepicker.onButtonClick(event);
    requestAnimationFrame(() => {
      this.inputRef.nativeElement.focus();
    });
  }

  dateChange(event: Event) {
    this.isValid.set(false);

    const inputValue = event.target as HTMLInputElement;

    const array = inputValue.value.split('.');

    if (array[1]) {
      array[0] = array[0].padStart(2, '0');
    }
    if (array[2]) {
      array[1] = array[1].padStart(2, '0');
      if (+array[2] > 2025) {
        array[2] = '2025';
      }
    }

    if (isValidDate(inputValue.value)) {
      this.value.set(inputValue.value);
      this.isValid.set(true);
      this.onChange(inputValue.value);
    }
  }

  calendarChange(event: any) {
    const date = this.datePipe.transform(event, 'dd.MM.yyyy') || '';
    this.value.set(date);
    this.onChange(date);
  }

  writeValue(date: string): void {
    if (date) {
      const formatDate = this.datePipe.transform(date, 'dd.MM.yyyy');
      this.value.set(formatDate as string);
      return;
    }
    this.value.set(date);
  }
  registerOnChange(fn: FunctionType): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: FunctionType): void {
    this.onTouched = fn;
  }
  setDisabledState(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled);
  }
}
